// frontend/src/pages/Profile/OrderHistory.jsx
import React, { useState, useEffect } from 'react';
import { profileService } from '../components/services/profileServices';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import AlertMessage from '../components/ui/AlertMessage';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import './order-history.scss';

const OrderHistory = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [orderToCancel, setOrderToCancel] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        setLoading(true);
        setError(null);
        const result = await profileService.getUserOrders();
        
        if (result.success) {
            setOrders(result.orders || []);
        } else {
            setError(result.message || 'Impossibile caricare gli ordini');
        }
        setLoading(false);
    };

    const handleViewOrder = async (order) => {
        setLoading(true);
        const result = await profileService.getOrderDetails(order.numero_ordine || order.id);
        if (result.success) {
            setSelectedOrder(result.order || result.data);
        } else {
            setError('Impossibile caricare i dettagli dell\'ordine');
        }
        setLoading(false);
    };

    const handleCancelOrder = async () => {
        if (!orderToCancel) return;
        
        setCancelling(true);
        const result = await profileService.cancelOrder(orderToCancel.numero_ordine);
        
        if (result.success) {
            await loadOrders();
            setShowCancelModal(false);
            setOrderToCancel(null);
            if (selectedOrder) setSelectedOrder(null);
        } else {
            setError(result.message || 'Errore durante l\'annullamento');
        }
        setCancelling(false);
    };

    const getStatusBadge = (status) => {
        const statusInfo = profileService.getOrderStatus(status);
        return (
            <span className={`status-badge ${statusInfo.class}`}>
                <span className="status-icon">{statusInfo.icon}</span>
                <span className="status-label">{statusInfo.label}</span>
            </span>
        );
    };

    const formatPrice = (price) => profileService.formatPrice(price);
    const formatDate = (date) => profileService.formatDate(date);
    const formatShortDate = (date) => profileService.formatShortDate(date);

    if (loading && orders.length === 0) {
        return <LoadingSpinner message="Caricamento ordini..." />;
    }

    if (error) {
        return <AlertMessage type="error" message={error} />;
    }

    if (orders.length === 0) {
        return (
            <div className="order-empty-state">
                <div className="empty-icon">📦</div>
                <h3>Nessun ordine trovato</h3>
                <p>Non hai ancora effettuato ordini.</p>
                <button 
                    className="btn-shop"
                    onClick={() => window.location.href = '/negozioonline'}
                >
                    🛍️ Vai allo Shop
                </button>
            </div>
        );
    }

    return (
        <div className="order-history">
            <div className="section-header">
                <h2>📦 I Miei Ordini</h2>
                <span className="order-count">Totale: {orders.length} ordini</span>
            </div>

            {/* Lista Ordini */}
            <div className="orders-list">
                {orders.map(order => (
                    <div key={order.id} className="order-card">
                        <div className="order-header">
                            <div className="order-info">
                                <span className="order-number">
                                    #{order.numero_ordine || order.id}
                                </span>
                                <span className="order-date">
                                    📅 {formatShortDate(order.data_creazione)}
                                </span>
                            </div>
                            {getStatusBadge(order.stato)}
                        </div>

                        <div className="order-details">
                            <div className="order-summary">
                                <div className="summary-item">
                                    <label>Totale</label>
                                    <strong>{formatPrice(order.importo_totale)}</strong>
                                </div>
                                <div className="summary-item">
                                    <label>Articoli</label>
                                    <span>{order.numero_articoli || '-'}</span>
                                </div>
                                <div className="summary-item">
                                    <label>Spedizione</label>
                                    <span>{order.metodo_spedizione || 'Standard'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="order-actions">
                            <button 
                                className="btn-view"
                                onClick={() => handleViewOrder(order)}
                            >
                                👁️ Visualizza Dettagli
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal Dettaglio Ordine */}
            {selectedOrder && (
                <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="modal-content order-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3># {selectedOrder.numero_ordine}</h3>
                            <button className="modal-close" onClick={() => setSelectedOrder(null)}>×</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="detail-section">
                                <div className="detail-row">
                                    <span className="detail-label">Data:</span>
                                    <span className="detail-value">{formatDate(selectedOrder.data_creazione)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Stato:</span>
                                    <span className="detail-value">{getStatusBadge(selectedOrder.stato)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Totale:</span>
                                    <span className="detail-value total">{formatPrice(selectedOrder.importo_totale)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Spedizione:</span>
                                    <span className="detail-value">{selectedOrder.metodo_spedizione || 'Standard'}</span>
                                </div>
                                {selectedOrder.note_cliente && (
                                    <div className="detail-row">
                                        <span className="detail-label">Note:</span>
                                        <span className="detail-value">{selectedOrder.note_cliente}</span>
                                    </div>
                                )}
                            </div>

                            {/* Indirizzi */}
                            {(selectedOrder.indirizzo_spedizione || selectedOrder.indirizzo_fatturazione) && (
                                <div className="addresses-section-detail">
                                    {selectedOrder.indirizzo_spedizione && (
                                        <div className="address-detail">
                                            <h4>📦 Indirizzo di Spedizione</h4>
                                            <p>{selectedOrder.indirizzo_spedizione.indirizzo}</p>
                                            <p>{selectedOrder.indirizzo_spedizione.citta}, {selectedOrder.indirizzo_spedizione.provincia} {selectedOrder.indirizzo_spedizione.cap}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Articoli */}
                            {selectedOrder.articoli && selectedOrder.articoli.length > 0 && (
                                <div className="order-items-list">
                                    <h4>🛒 Articoli Ordinati</h4>
                                    <div className="items-header">
                                        <span>Prodotto</span>
                                        <span>Q.tà</span>
                                        <span>Prezzo</span>
                                    </div>
                                    {selectedOrder.articoli.map((item, idx) => (
                                        <div key={idx} className="order-item">
                                            <span className="item-name">{item.nome_prodotto}</span>
                                            <span className="item-quantity">x{item.quantita}</span>
                                            <span className="item-price">{formatPrice(item.prezzo_totale || item.prezzo_unitario * item.quantita)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-close" onClick={() => setSelectedOrder(null)}>
                                Chiudi
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Conferma Annullamento */}
            <ConfirmationModal
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false);
                    setOrderToCancel(null);
                }}
                onConfirm={handleCancelOrder}
                title="Annulla Ordine"
                message={`Sei sicuro di voler annullare l'ordine #${orderToCancel?.numero_ordine}? Questa operazione è irreversibile.`}
                confirmText={cancelling ? 'Annullamento...' : 'Sì, Annulla'}
                cancelText="No, Torna Indietro"
            />
        </div>
    );
};

export default OrderHistory;
